import React, { useState, useEffect } from 'react';
import {
  getBackendStatus,
  startBackend,
  stopBackend,
  restartBackend,
  getBetStatus,
  toggleBet,
  getBackendLogs,
} from '../services/api';

function BackendManager() {
  const [status, setStatus] = useState(null);
  const [betEnabled, setBetEnabled] = useState(true);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const [statusRes, betRes] = await Promise.all([
        getBackendStatus(),
        getBetStatus(),
      ]);
      setStatus(statusRes);
      setBetEnabled(betRes.bet_enabled);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load status' });
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      setMessage(null);
      const data = await getBackendLogs(2000);
      setLogs(data);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load logs' });
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleAction = async (action) => {
    setActionLoading(action);
    setMessage(null);
    try {
      let result;
      switch (action) {
        case 'start':
          result = await startBackend();
          break;
        case 'stop':
          result = await stopBackend();
          break;
        case 'restart':
          result = await restartBackend();
          break;
        default:
          return;
      }
      setMessage({ type: result.success ? 'success' : 'error', text: result.message });
      await loadStatus();
      if (action === 'restart' && result.success) {
        setTimeout(loadLogs, 3000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Action failed' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleBetToggle = async () => {
    setActionLoading('bet');
    setMessage(null);
    try {
      const result = await toggleBet(!betEnabled);
      if (result.success) {
        setBetEnabled(result.bet_enabled);
        setMessage({ type: 'success', text: `Betting ${result.bet_enabled ? 'enabled' : 'disabled'}` });
      } else {
        setMessage({ type: 'error', text: result.message || 'Toggle failed' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Toggle failed' });
    } finally {
      setActionLoading(null);
    }
  };

  const running = status?.running ?? false;

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-6">Backend Manager</h1>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-orange-900/30 text-orange-200 border border-orange-600' : 'bg-red-900/30 text-red-200 border border-red-600'}`}>
            {message.text}
          </div>
        )}

        <div className="bg-zinc-900 rounded-lg shadow p-6 mb-6 border border-zinc-800">
          <h2 className="text-lg font-semibold text-white mb-4">Main Backend (Dice Game)</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-gray-400">
              <div className="animate-spin h-5 w-5 border-2 border-orange-500 border-t-transparent rounded-full" />
              Loading status...
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${running ? 'bg-orange-900/50 text-orange-400' : 'bg-red-900/50 text-red-400'}`}>
                  <span className={`w-2 h-2 rounded-full ${running ? 'bg-orange-500' : 'bg-red-500'}`} />
                  {running ? 'Running' : 'Stopped'}
                </span>
                {status?.pid && <span className="text-sm text-gray-400">PID: {status.pid}</span>}
              </div>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => handleAction('start')} disabled={running || actionLoading}
                  className="px-4 py-2 bg-orange-500 text-black rounded-lg hover:bg-orange-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                  {actionLoading === 'start' ? 'Starting...' : 'Start'}
                </button>
                <button onClick={() => handleAction('stop')} disabled={!running || actionLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                  {actionLoading === 'stop' ? 'Stopping...' : 'Stop'}
                </button>
                <button onClick={() => handleAction('restart')} disabled={!running || actionLoading}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                  {actionLoading === 'restart' ? 'Restarting...' : 'Restart'}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="bg-zinc-900 rounded-lg shadow p-6 mb-6 border border-zinc-800">
          <h2 className="text-lg font-semibold text-white mb-4">Bet Switch</h2>
          <p className="text-gray-400 text-sm mb-4">Enable or disable betting. When disabled, new deposits will not create bets.</p>
          <div className="flex items-center gap-4">
            <span className={`font-medium ${betEnabled ? 'text-orange-400' : 'text-red-400'}`}>
              {betEnabled ? 'Betting enabled' : 'Betting disabled'}
            </span>
            <button onClick={handleBetToggle} disabled={actionLoading === 'bet'}
              className={`px-4 py-2 rounded-lg font-medium ${betEnabled ? 'bg-red-900/50 text-red-300 hover:bg-red-800/50' : 'bg-orange-900/50 text-orange-300 hover:bg-orange-800/50'} disabled:opacity-50`}>
              {actionLoading === 'bet' ? '...' : betEnabled ? 'Disable betting' : 'Enable betting'}
            </button>
          </div>
        </div>

        <div className="bg-zinc-900 rounded-lg shadow p-6 border border-zinc-800">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-white">Main Backend Logs</h2>
            <button onClick={loadLogs} className="px-4 py-2 bg-orange-500 text-black rounded-lg hover:bg-orange-400 flex items-center gap-2 font-medium">
              <span>🔄</span> Refresh
            </button>
          </div>
          {logs?.logs && logs.logs.length > 0 ? (
            <div className="bg-black text-green-400 font-mono text-sm rounded-lg p-4 h-[600px] overflow-y-auto border border-zinc-700">
              {logs.logs.map((line, index) => (
                <div key={index} className="whitespace-pre-wrap break-words">{line}</div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">Click "Refresh" to load backend logs.</p>
          )}
          {logs?.logs && logs.logs.length > 0 && (
            <p className="text-sm text-gray-500 mt-2">Showing last {logs.logs.length} lines.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default BackendManager;
